// Single home for converting dates between the API and the UI.
//
// Contract:
// - Outgoing (requests): every date/datetime is sent as an ISO-8601
//   OffsetDateTime carrying the browser's own UTC offset, e.g.
//   "2026-09-30T17:30:00+05:30" — never a bare local value and never
//   `toISOString()` (which rewrites the wall-clock time as UTC "Z").
// - Incoming (responses): an OffsetDateTime/"Z" value is an instant, shown
//   and edited in the browser's local timezone. A bare "YYYY-MM-DD" is a
//   calendar date with no timezone, so it's kept as that same day.
//
// Display formatting lives in lib/dateFormat.ts (`formatDate`), which
// parses through `parseApiDate` below.

export type DateInput = string | Date | null | undefined;

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;
const LOCAL_DATE_TIME = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/;
const TIME_ONLY = /^(\d{2}):(\d{2})(?::\d{2})?$/;

const pad2 = (n: number) => String(n).padStart(2, "0");
const isValid = (date: Date) => !Number.isNaN(date.getTime());

const formatLocalDate = (date: Date) => `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
const formatLocalTime = (date: Date) => `${pad2(date.getHours())}:${pad2(date.getMinutes())}`;

// "+05:30" / "-04:00" for the given date — computed per date, so DST is
// resolved for that day rather than for today.
export const getUtcOffset = (date: Date) => {
  const totalMinutes = -date.getTimezoneOffset();
  const sign = totalMinutes >= 0 ? "+" : "-";
  const absMinutes = Math.abs(totalMinutes);
  return `${sign}${pad2(Math.floor(absMinutes / 60))}:${pad2(absMinutes % 60)}`;
};

// A local form value ("YYYY-MM-DD" → local midnight, or
// "YYYY-MM-DDTHH:mm[:ss]") as a `Date`. Built with the numeric constructor,
// which is always local time — `new Date(string)` reads a bare date as UTC.
const parseLocalInput = (value: string): Date | null => {
  const match = value.match(LOCAL_DATE_TIME) ?? value.match(DATE_ONLY);
  if (!match) return null;
  const [, year, month, day, hour = "0", minute = "0", second = "0"] = match;
  const date = new Date(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute), Number(second));
  return isValid(date) ? date : null;
};

/**
 * Outgoing: a `Date` or a local form value ("YYYY-MM-DD",
 * "YYYY-MM-DDTHH:mm[:ss]") → OffsetDateTime in the browser's timezone.
 * A date-only value becomes local midnight of that day.
 *
 * toOffsetDateTime("2026-09-30T17:30") // "2026-09-30T17:30:00+05:30" (India)
 * toOffsetDateTime("2026-09-30")       // "2026-09-30T00:00:00+05:30"
 * toOffsetDateTime(new Date())         // now, e.g. "2026-10-01T14:30:05+05:30"
 *
 * A `Date` always converts (so the result is a plain `string`); a form
 * string can be empty/malformed, so that returns `null`.
 */
export function toOffsetDateTime(value: Date): string;
export function toOffsetDateTime(value: DateInput): string | null;
export function toOffsetDateTime(value: DateInput): string | null {
  if (!value) return null;
  const date = value instanceof Date ? value : parseLocalInput(value);
  if (!date || !isValid(date)) return null;
  return `${formatLocalDate(date)}T${formatLocalTime(date)}:${pad2(date.getSeconds())}${getUtcOffset(date)}`;
}

/**
 * Incoming: an API value → `Date`. OffsetDateTime/"Z" values parse as the
 * instant they are; a bare "YYYY-MM-DD" is that calendar day at local
 * midnight (so it never shows as the previous day west of UTC).
 */
export const parseApiDate = (value: DateInput | number): Date | null => {
  if (value === null || value === undefined || value === "") return null;
  if (value instanceof Date) return isValid(value) ? value : null;
  if (typeof value === "string" && DATE_ONLY.test(value)) return parseLocalInput(value);
  const date = new Date(value);
  return isValid(date) ? date : null;
};

/** API value → "YYYY-MM-DD" for a date input, in local time. "" if invalid. */
export const toDateInputValue = (value: DateInput): string => {
  const date = parseApiDate(value);
  return date ? formatLocalDate(date) : "";
};

/**
 * API value → "HH:mm" for a time input, in local time. A legacy bare
 * "HH:mm[:ss]" (no date, so no timezone to convert) is kept as-is.
 */
export const toTimeInputValue = (value: DateInput): string => {
  const legacy = typeof value === "string" ? value.match(TIME_ONLY) : null;
  if (legacy) return `${legacy[1]}:${legacy[2]}`;
  const date = parseApiDate(value);
  return date ? formatLocalTime(date) : "";
};

/** API value → "YYYY-MM-DDTHH:mm" for a datetime input, in local time. */
export const toDateTimeInputValue = (value: DateInput): string => {
  const date = parseApiDate(value);
  return date ? `${formatLocalDate(date)}T${formatLocalTime(date)}` : "";
};

/** Today's local date as "YYYY-MM-DD" — for date inputs' `min`/`max`. */
export const todayInputValue = () => formatLocalDate(new Date());
